import { create } from 'zustand';
import { assignmentRequestsApi } from '../lib/api';
import type { AssignmentRequestListItem, AssignmentGrantScope, AssignmentRequestFilter } from '../types';

interface AssignmentRequestState {
  requests: AssignmentRequestListItem[];
  pendingCount: number;
  myOpenCount: number;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  view: 'queue' | 'mine';
  statusFilter: AssignmentRequestFilter;
  fetchQueue: (status?: AssignmentRequestFilter) => Promise<void>;
  fetchMine: (status?: AssignmentRequestFilter) => Promise<void>;
  fetchCount: () => Promise<void>;
  fetchMyOpenCount: () => Promise<void>;
  approve: (id: string, scope: AssignmentGrantScope) => Promise<void>;
  deny: (id: string, note?: string) => Promise<void>;
  hold: (id: string, note?: string) => Promise<void>;
  cancel: (id: string) => Promise<void>;
}

async function refresh(get: () => AssignmentRequestState) {
  const { view, statusFilter } = get();
  await Promise.all([
    view === 'mine' ? get().fetchMine(statusFilter) : get().fetchQueue(statusFilter),
    get().fetchCount(),
  ]);
}

export const useAssignmentRequestStore = create<AssignmentRequestState>((set, get) => ({
  requests: [],
  pendingCount: 0,
  myOpenCount: 0,
  isLoading: false,
  isSaving: false,
  error: null,
  view: 'queue',
  statusFilter: 'PENDING',

  fetchQueue: async (status = 'PENDING') => {
    set({ isLoading: true, error: null, view: 'queue', statusFilter: status });
    try {
      const { requests } = await assignmentRequestsApi.listQueue(status);
      set({ requests, isLoading: false });
    } catch (e: any) {
      set({ isLoading: false, error: e.response?.data?.message || 'Failed to load requests' });
    }
  },
  fetchMine: async (status = 'PENDING') => {
    set({ isLoading: true, error: null, view: 'mine', statusFilter: status });
    try {
      const { requests } = await assignmentRequestsApi.listMine(status);
      set({ requests, isLoading: false });
    } catch (e: any) {
      set({ isLoading: false, error: e.response?.data?.message || 'Failed to load requests' });
    }
  },
  fetchCount: async () => {
    try {
      const { count } = await assignmentRequestsApi.count('PENDING');
      set({ pendingCount: count });
    } catch { /* badge is best-effort */ }
  },
  fetchMyOpenCount: async () => {
    try {
      const { count } = await assignmentRequestsApi.count('OPEN', 'mine');
      set({ myOpenCount: count });
    } catch { /* badge is best-effort */ }
  },

  approve: async (id, scope) => {
    set({ isSaving: true });
    try { await assignmentRequestsApi.approve(id, scope); await refresh(get); }
    catch (e: any) { throw new Error(e.response?.data?.message || 'Failed to approve'); }
    finally { set({ isSaving: false }); }
  },
  deny: async (id, note) => {
    set({ isSaving: true });
    try { await assignmentRequestsApi.deny(id, note); await refresh(get); }
    catch (e: any) { throw new Error(e.response?.data?.message || 'Failed to deny'); }
    finally { set({ isSaving: false }); }
  },
  hold: async (id, note) => {
    set({ isSaving: true });
    try { await assignmentRequestsApi.hold(id, note); await refresh(get); }
    catch (e: any) { throw new Error(e.response?.data?.message || 'Failed to hold'); }
    finally { set({ isSaving: false }); }
  },
  cancel: async (id) => {
    set({ isSaving: true });
    try { await assignmentRequestsApi.cancel(id); await refresh(get); }
    catch (e: any) { throw new Error(e.response?.data?.message || 'Failed to cancel'); }
    finally { set({ isSaving: false }); }
  },
}));
